import React, { useEffect, useState } from 'react';
import { UserCheck } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import {
  findingService,
  type Finding,
  type FindingAllocatePayload,
} from '../../services/auditService';
import apiClient from '../../services/apiService';
import { extractApiError } from '../../utils/apiError';
import { isPickableUser } from '../../utils/userPickable';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedTextarea } from '../../validation/ValidatedFields';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';

/**
 * Phase 08 — AllocateFindingModal.
 *
 * Opened from FindingDetailModal footer by GRC Admin / GRC Audit Head to
 * assign a released finding to an org member for response. Allocated user
 * sees the finding on staff portal "Findings Allocated to Me" bucket.
 *
 * User picker reads tenant org members via the existing usr/users-in-entity
 * endpoint with a search/list response shape compatible with backend tenant
 * scoping.
 */

interface AllocateUserOption {
  id: number;
  full_name: string;
  email?: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  finding: Finding | null;
  onAllocated: (updated: Finding) => void;
}

const AllocateFindingModal: React.FC<Props> = ({ isOpen, onClose, finding, onAllocated }) => {
  const [users, setUsers] = useState<AllocateUserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [dueDate, setDueDate] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const vh = useFieldValidation();
  const { allowed: canModify, loading: permLoading } = usePermission('ADT_FINDINGS', 'view+modify');

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSubmitting(false);
    setUserId(finding?.allocated_to_user_id ?? null);
    setDueDate(finding?.allocation_due_date ?? '');
    setNote(finding?.allocation_note ?? '');
    vh.reset();
  }, [isOpen, finding]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingUsers(true);
    apiClient
      .get('/api/v1/usr/users', { params: { limit: 500 } })
      .then((resp: any) => {
        if (cancelled) return;
        const raw = resp?.data?.data ?? resp?.data ?? [];
        const opts: AllocateUserOption[] = (Array.isArray(raw) ? raw : []).filter(isPickableUser).map((u: any) => ({
          id: u.id,
          full_name:
            (u.first_name || u.last_name)
              ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()
              : (u.user_name ?? u.login_id ?? `User #${u.id}`),
          email: u.email ?? u.login_id ?? null,
        }));
        setUsers(opts);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!finding) return null;

  const handleSubmit = async () => {
    if (submitting) return;
    if (!canModify) { setError(NOT_AUTHORISED_MSG); return; }
    if (userId == null) {
      vh.setError('userId', 'Please select a user to allocate to.');
      return;
    }
    const fields: Array<{ name: string; code: number; value: string }> = [];
    if (dueDate) fields.push({ name: 'dueDate', code: 305, value: dueDate });
    if (note.trim()) fields.push({ name: 'note', code: 110, value: note });
    if (!vh.validateAll(fields)) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: FindingAllocatePayload = {
        allocated_to_user_id: userId,
        allocation_due_date: dueDate || null,
        allocation_note: note.trim() || null,
      };
      const updated = await findingService.allocate(finding.id, payload);
      onAllocated(updated);
      onClose();
    } catch (e: any) {
      setError(extractApiError(e, 'Failed to allocate finding.'));
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnEscape closeOnOverlayClick data-testid="MOD_allocate_finding_root">
      <Modal.Header>
        <span className="modal-header-icon-row">
          <UserCheck size={16} />
          Allocate Finding for Response
        </span>
      </Modal.Header>
      <Modal.Body>
        <div className="observation-context-card">
          <div className="observation-context-title">
            {finding.findings_text.slice(0, 120)}
          </div>
          <div className="observation-context-row">
            {finding.program_name ?? '—'}
            {finding.procedure_name ? ` · ${finding.procedure_name}` : ''}
          </div>
        </div>

        <div className="assign-form-block">
          <FormControl.Select
            label="Allocate to"
            required
            value={userId ?? ''}
            disabled={loadingUsers}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              setUserId(e.target.value ? Number(e.target.value) : null);
              vh.clearError('userId');
            }}
            data-testid="SEL_allocate_finding_user"
          >
            <option value="">{loadingUsers ? 'Loading users…' : 'Select user…'}</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name}
                {u.email ? ` — ${u.email}` : ''}
              </option>
            ))}
          </FormControl.Select>
          {vh.errors.userId && <div className="inline-error inline-error--field">{vh.errors.userId}</div>}
        </div>

        <div className="assign-form-block">
          <FormControl.DatePicker
            label="Response due date"
            value={dueDate}
            onChange={(e) => { setDueDate(e.target.value); vh.clearError('dueDate'); }}
            onBlur={() => dueDate && vh.validateField('dueDate', 305, dueDate)}
            error={vh.errors.dueDate}
            data-testid="INP_allocate_finding_due_date"
          />
        </div>

        <div className="assign-form-block">
          <ValidatedTextarea
            vh={vh}
            name="note"
            code={110}
            required={false}
            label="Note (optional)"
            rows={3}
            value={note}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
            placeholder="Context or instructions for the assignee…"
            data-testid="INP_allocate_finding_note"
          />
        </div>

        {error && (
          <div className="inline-error inline-error--inline" role="alert">
            {error}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary-outline" onClick={onClose} disabled={submitting} data-testid="BTN_allocate_finding_cancel">
          Cancel
        </Button>
        <Button variant="fill" onClick={handleSubmit} disabled={permLoading || !canModify || submitting} title={!canModify ? NOT_AUTHORISED_MSG : undefined} data-testid="BTN_allocate_finding_submit">
          {submitting ? 'Allocating…' : finding.allocated_to_user_id ? 'Re-allocate' : 'Allocate'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AllocateFindingModal;
